import React, { useState, useEffect, useRef } from 'react';
import Modal from 'react-modal';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import { X, Download } from 'lucide-react';
import { getDocumentUrl } from '../services/cdnService';

// Set the app element for accessibility
Modal.setAppElement('#root');

const CitationModal = ({ isOpen, onRequestClose, fileUrl, pageNumber, filename }) => {
  const [textContent, setTextContent] = useState('');
  const isWordFile = filename.toLowerCase().endsWith('.docx') || filename.toLowerCase().endsWith('.doc');
  const isTextFile = filename.toLowerCase().endsWith('.txt');
  const iframeRef = useRef(null);
  const [displayUrl, setDisplayUrl] = useState(fileUrl);

  useEffect(() => {
    const loadDocument = async () => {
      if (fileUrl) {
        console.log('Original fileUrl:', fileUrl);
        
        // Extract the actual file path from the URL
        let filePath = fileUrl;
        
        // If it's already a CloudFront URL, don't try to transform it again
        if (fileUrl.includes(process.env.REACT_APP_CLOUDFRONT_DOMAIN)) {
          setDisplayUrl(fileUrl);
          return;
        }
        
        // If it's a Supabase URL, extract the path
        if (fileUrl.includes('storage/v1/object/sign/')) {
          const matches = fileUrl.match(/chat-case-files\/(.*?)\?/);
          if (matches && matches[1]) {
            filePath = matches[1];
            console.log('Extracted filePath:', filePath);
            const cdnUrl = await getDocumentUrl('chat-case-files', filePath);
            console.log('Generated CDN URL:', cdnUrl);
            if (cdnUrl) {
              setDisplayUrl(cdnUrl);
            } else {
              setDisplayUrl(fileUrl); // Fallback to direct Supabase URL
            }
          }
        } else {
          // If it's a direct path, use it as is
          const cdnUrl = await getDocumentUrl('chat-case-files', filePath);
          if (cdnUrl) {
            setDisplayUrl(cdnUrl);
          } else {
            setDisplayUrl(fileUrl);
          }
        }
        
        console.log('Final displayUrl:', displayUrl);
      }
    };
    
    loadDocument();
  }, [fileUrl]);

  useEffect(() => {
    if (isWordFile && iframeRef.current) {
      const handleError = (e) => {
        if (e.target.src.includes('cdn.office.net') && e.target.src.includes('.ttf')) {
          e.preventDefault();
        }
      };

      iframeRef.current.addEventListener('error', handleError, true);

      return () => {
        if (iframeRef.current) {
          iframeRef.current.removeEventListener('error', handleError, true);
        }
      };
    }
  }, [isWordFile]);

  const fetchTextContent = async () => {
    try {
      const response = await fetch(fileUrl);
      const text = await response.text();
      setTextContent(text);
    } catch (error) {
      console.error('Error fetching text content:', error);
      setTextContent('Error loading text content.');
    }
  };

  const truncateFilename = (name) => {
    if (name.length <= 90) return name;
    return name.slice(0, 83) + '...' + name.slice(-7);
  };

  const modalStyles = {
    content: {
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
      width: '80%',
      height: '80%',
      padding: '20px',
      display: 'flex',
      flexDirection: 'column',
    },
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      style={modalStyles}
      contentLabel="Document Viewer"
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <button onClick={onRequestClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
          <X size={20} />
        </button>
        <h2 style={{ margin: '0 10px', textAlign: 'center', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {truncateFilename(filename)}
        </h2>
        <a href={fileUrl} download={filename} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
          <Download size={20} />
        </a>
      </div>
      <div style={{ flex: 1, overflow: 'hidden' }}>
        {isWordFile ? (
          <iframe
            ref={iframeRef}
            src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`}
            width="100%"
            height="100%"
            frameBorder="0"
            style={{ border: 'none', width: '100%', height: '100%' }}
          >
            This is an embedded <a target='_blank' href='https://office.com'>Microsoft Office</a> document, powered by <a target='_blank' href='https://office.com/webapps'>Office</a>.
          </iframe>
        ) : isTextFile ? (
          <div style={{ width: '100%', height: '100%', overflow: 'auto', whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
            {textContent}
          </div>
        ) : (
          <Worker workerUrl={`https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js`}>
            {displayUrl ? (
              <Viewer 
                fileUrl={displayUrl} 
                defaultScale={1.5} 
                initialPage={pageNumber - 1} 
              />
            ) : (
              <div>Loading document...</div>
            )}
          </Worker>
        )}
      </div>
    </Modal>
  );
};

export default CitationModal;
